import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  CheckCircle,
  AlertCircle,
  Building2,
  RotateCcw,
  RefreshCw,
  Loader2
} from 'lucide-react';
import {
  getSales,
  getSaleById,
  getAwaitingConfirmation,
  confirmPayment,
  reverseSale,
  unreverseSale
} from '../../api/sales';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import SaleDetailModal from '../../components/sales/SaleDetailModal';
import ReceiptPreviewModal from '../../components/receipts/ReceiptPreviewModal';
import BankChannelModal from '../../components/sales/BankChannelModal';
import { BranchSelector } from '../../components/branches';
import { useAuth } from '../../contexts/AuthContext';
import { useBranch } from '../../contexts/BranchContext';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import apiClient from '../../api/client';
import PeriodFilter from '../../components/common/PeriodFilter';

const SalesList = ({ defaultTab = 'all' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBranchId, selectBranch, isMultiBranch } = useBranch();
  const { periodRange } = useFiscalPeriod();
  const isAdmin = ['admin', 'super_admin', 'retail_admin', 'branch_admin', 'system_admin'].includes(
    user?.role
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(defaultTab); // 'all', 'pending', 'cancelled'

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [processingId, setProcessingId] = useState(null); // tracks which sale has an action in-flight
  const [filters, setFilters] = useState({
    paymentStatus: '',
    paymentMethod: ''
  });

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        startDate: localPeriod.start_date || '',
        endDate: localPeriod.end_date || '',
        label: localPeriod.label
      };
    }
    return {
      startDate: periodRange?.start_date || '',
      endDate: periodRange?.end_date || '',
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Receipt preview modal state
  const [receiptPreview, setReceiptPreview] = useState(null);

  // Bank Channel Modal for updating pending sales
  const [showBankModal, setShowBankModal] = useState(false);
  const [pendingUpdateSale, setPendingUpdateSale] = useState(null);

  // Check for showReceipt query param on load
  useEffect(() => {
    const showReceiptId = searchParams.get('showReceipt');
    if (showReceiptId) {
      loadReceiptPreview(parseInt(showReceiptId, 10));
      // Remove the query param from URL
      setSearchParams({});
    }
  }, []);

  const loadReceiptPreview = async (saleId) => {
    try {
      const response = await getSaleById(saleId);
      const sale = response.data || response;
      setReceiptPreview({
        saleId: sale.id,
        receiptNumber: sale.receipt_number,
        customerName: sale.customer_name,
        customerPhone: sale.customer_phone,
        customerEmail: sale.customer_email,
        totalAmount: sale.total_amount,
        saleDate: sale.sale_date
      });
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  // Fetch sales (only used for initial load, tab/filter/page changes)
  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      let response;

      if (activeTab === 'pending') {
        response = await getAwaitingConfirmation({
          page: pagination.page,
          limit: pagination.limit
        });
        setSales(response.data || []);
      } else {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          status: activeTab === 'cancelled' ? 'cancelled' : undefined,
          branch_id: isMultiBranch && currentBranchId ? currentBranchId : undefined
        };

        if (searchTerm) params.search = searchTerm;
        if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
        if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod;
        if (activeDateRange.startDate) params.startDate = activeDateRange.startDate;
        if (activeDateRange.endDate) params.endDate = activeDateRange.endDate;

        response = await getSales(params);

        let fetchedSales = response.sales || [];
        if (activeTab === 'all') {
          fetchedSales = fetchedSales.filter((s) => s.status !== 'cancelled');
        }

        setSales(fetchedSales);
      }

      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch sales');
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    pagination.page,
    pagination.limit,
    searchTerm,
    filters,
    currentBranchId,
    activeDateRange.startDate,
    activeDateRange.endDate
  ]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Debounce search term changes - reset to page 1 when search changes
  useEffect(() => {
    const debounce = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleFilterChange = (field, value) => {
    if (field === 'startDate' || field === 'endDate') {
      // When user manually changes dates, set as local override
      setLocalPeriod((prev) => ({
        start_date: field === 'startDate' ? value : prev?.start_date || activeDateRange.startDate,
        end_date: field === 'endDate' ? value : prev?.end_date || activeDateRange.endDate,
        label: 'Custom'
      }));
    } else {
      setFilters((prev) => ({ ...prev, [field]: value }));
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      paymentStatus: '',
      paymentMethod: ''
    });
    setLocalPeriod(null); // Reset to global period
    setSearchTerm('');
  };

  // Handle period filter change
  const handlePeriodChange = useCallback((range) => {
    // If user selects "Global Period" option (isGlobal flag), reset to use global settings
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleConfirmPayment = async (e, sale) => {
    e.stopPropagation();
    if (window.confirm(`Confirm payment for Receipt #${sale.receipt_number}?`)) {
      setProcessingId(sale.id);
      try {
        await confirmPayment(sale.id);
        toast.success('Payment confirmed successfully');
        setSales((prev) => prev.filter((s) => s.id !== sale.id));
        setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to confirm payment');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const handleAddBankInfo = (e, sale) => {
    e.stopPropagation();
    setPendingUpdateSale(sale);
    setShowBankModal(true);
  };

  const handleBankChannelSelect = async (channelData) => {
    const updatedSaleId = pendingUpdateSale.id;
    try {
      await apiClient.put(`/sales/${updatedSaleId}/bank-channel`, {
        bank_channel_id: channelData.bank_channel_id
      });
      toast.success('Bank channel updated');
      setShowBankModal(false);
      setPendingUpdateSale(null);
      setSales((prev) =>
        prev.map((s) =>
          s.id === updatedSaleId ? { ...s, confirmation_status: 'awaiting_confirmation' } : s
        )
      );
    } catch (error) {
      toast.error('Failed to update bank channel');
    }
  };

  const handleReverseSale = async (sale) => {
    if (
      window.confirm(
        `Are you sure you want to reverse Sale #${sale.receipt_number}? This will restore stock and reverse payments.`
      )
    ) {
      setProcessingId(sale.id);
      try {
        await reverseSale(sale.id);
        toast.success('Sale reversed successfully');
        if (activeTab === 'all') {
          setSales((prev) => prev.filter((s) => s.id !== sale.id));
          setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        } else {
          setSales((prev) =>
            prev.map((s) => (s.id === sale.id ? { ...s, status: 'cancelled' } : s))
          );
        }
        if (selectedSaleId === sale.id) setSelectedSaleId(null);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reverse sale');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const handleUnreverseSale = async (sale) => {
    if (
      window.confirm(
        `Reactivate Sale #${sale.receipt_number}? This will deduct stock again and restore analytics.`
      )
    ) {
      setProcessingId(sale.id);
      try {
        await unreverseSale(sale.id);
        toast.success('Sale reactivated successfully');
        setSales((prev) => prev.filter((s) => s.id !== sale.id));
        setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reactivate sale');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const getConfirmationBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-800',
      awaiting_confirmation: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      confirmed: 'Confirmed',
      awaiting_confirmation: 'Awaiting Confirm',
      pending: 'Pending Info'
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      unpaid: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentMethodBadge = (method) => {
    const badges = {
      cash: 'bg-blue-100 text-blue-800',
      transfer: 'bg-purple-100 text-purple-800',
      pos: 'bg-indigo-100 text-indigo-800',
      credit: 'bg-orange-100 text-orange-800'
    };
    return badges[method] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary-black">Sales</h1>
              <p className="text-xs sm:text-sm text-secondary-gray">
                Manage all sales transactions
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Branch Selector - shows in multi-branch mode */}
              {isMultiBranch && (
                <BranchSelector
                  selectedBranchId={currentBranchId}
                  onBranchChange={selectBranch}
                  showAllOption={true}
                />
              )}
              <PeriodFilter onChange={handlePeriodChange} compact={true} storageKey="sales_list" />
              <button
                type="button"
                onClick={() => navigate('/sales/new')}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="whitespace-nowrap text-sm sm:text-base">New Sale</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-4 border-b border-gray-200 overflow-x-auto">
          <button
            className={`pb-2 px-1 whitespace-nowrap ${activeTab === 'all' ? 'border-b-2 border-accent-gold font-bold text-accent-gold' : 'text-gray-500'}`}
            onClick={() => {
              setActiveTab('all');
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            All Sales
          </button>
          {isAdmin && (
            <button
              className={`pb-2 px-1 whitespace-nowrap ${activeTab === 'pending' ? 'border-b-2 border-accent-gold font-bold text-accent-gold' : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('pending');
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              Pending Confirmations
            </button>
          )}
          <button
            className={`pb-2 px-1 whitespace-nowrap ${activeTab === 'cancelled' ? 'border-b-2 border-accent-gold font-bold text-accent-gold' : 'text-gray-500'}`}
            onClick={() => {
              setActiveTab('cancelled');
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            Cancelled / Reversed
          </button>
        </div>

        {/* Filters (Only for All Sales and Cancelled) */}
        {activeTab !== 'pending' && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by receipt, customer, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  />
                </div>
              </div>

              {/* Payment Status Filter */}
              <div>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                >
                  <option value="">All Payment Status</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>

              {/* Payment Method Filter */}
              <div>
                <select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                >
                  <option value="">All Methods</option>
                  <option value="cash">Cash</option>
                  <option value="transfer">Transfer</option>
                  <option value="pos">POS</option>
                  <option value="credit">Credit</option>
                </select>
              </div>

              {/* Clear Filters */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex-1 px-4 py-2 bg-white border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors"
                >
                  <span className="whitespace-nowrap">Clear</span>
                </button>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
              <div>
                <label className="block text-xs text-secondary-gray mb-1">
                  Start Date
                  {localPeriod && (
                    <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                      {activeDateRange.label}
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  value={activeDateRange.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-secondary-gray mb-1">End Date</label>
                <input
                  type="date"
                  value={activeDateRange.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Sales Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <LoadingSpinner message="Loading sales..." className="py-12" />
          ) : sales.length === 0 ? (
            <div className="p-8 text-center text-secondary-gray">
              <p className="text-sm mb-4">No sales found</p>
              {activeTab === 'all' && (
                <button
                  type="button"
                  onClick={() => navigate('/sales/new')}
                  className="px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap text-sm">Create First Sale</span>
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-primary-black text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Receipt #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Branch</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Details</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Amount</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Payment</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                      {(activeTab === 'pending' || isAdmin) && (
                        <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-lightGray">
                    {sales.map((sale) => (
                      <tr
                        key={sale.id}
                        className="hover:bg-secondary-offWhite transition-colors cursor-pointer"
                        onClick={() => setSelectedSaleId(sale.id)}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-primary-black">{sale.receipt_number}</p>
                          {sale.joint_sale_number && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                              <Building2 className="w-3 h-3" />
                              Joint: {sale.joint_sale_number}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-secondary-gray">
                            {new Date(sale.sale_date).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-secondary-gray">
                            {new Date(sale.sale_date).toLocaleTimeString()}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          {sale.branch_name ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {sale.branch_name}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-primary-black">{sale.customer_name}</p>
                          <p className="text-xs text-secondary-gray">{sale.customer_phone}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-secondary-gray">
                            {sale.total_items || 0} items
                          </p>
                          {sale.channel_name && (
                            <p className="text-xs text-blue-600">{sale.channel_name}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-semibold text-accent-gold">
                            ₦{parseFloat(sale.total_amount).toLocaleString()}
                          </p>
                          {sale.estimated_channel_charge > 0 && (
                            <p className="text-xs text-red-500">
                              Charge: ₦{parseFloat(sale.estimated_channel_charge).toLocaleString()}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadge(sale.payment_method)}`}
                          >
                            {sale.payment_method?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {activeTab === 'pending' ? (
                            getConfirmationBadge(sale.confirmation_status || 'confirmed')
                          ) : (
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${sale.status === 'cancelled' ? 'bg-red-100 text-red-800' : getPaymentStatusBadge(sale.payment_status)}`}
                            >
                              {sale.status === 'cancelled'
                                ? 'Cancelled'
                                : sale.payment_status.charAt(0).toUpperCase() +
                                  sale.payment_status.slice(1)}
                            </span>
                          )}
                        </td>
                        {(activeTab === 'pending' || isAdmin) && (
                          <td
                            className="px-4 py-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex justify-center gap-2">
                              {activeTab === 'pending' && (
                                <>
                                  {isAdmin &&
                                    sale.confirmation_status === 'awaiting_confirmation' && (
                                      <button
                                        onClick={(e) => handleConfirmPayment(e, sale)}
                                        disabled={processingId === sale.id}
                                        className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-wait"
                                        title="Confirm Payment"
                                      >
                                        {processingId === sale.id ? (
                                          <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                          <CheckCircle className="w-5 h-5" />
                                        )}
                                      </button>
                                    )}
                                  {sale.confirmation_status === 'pending' && (
                                    <button
                                      onClick={(e) => handleAddBankInfo(e, sale)}
                                      className="p-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                      title="Add Bank Info"
                                    >
                                      <Building2 className="w-5 h-5" />
                                    </button>
                                  )}
                                </>
                              )}
                              {activeTab !== 'pending' &&
                                isAdmin &&
                                sale.status !== 'cancelled' && (
                                  <button
                                    onClick={() => handleReverseSale(sale)}
                                    disabled={processingId === sale.id}
                                    className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-wait"
                                    title="Reverse Sale"
                                  >
                                    {processingId === sale.id ? (
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                      <RotateCcw className="w-5 h-5" />
                                    )}
                                  </button>
                                )}
                              {activeTab === 'cancelled' &&
                                isAdmin &&
                                sale.status === 'cancelled' && (
                                  <button
                                    onClick={() => handleUnreverseSale(sale)}
                                    disabled={processingId === sale.id}
                                    className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-wait"
                                    title="Reactivate Sale"
                                  >
                                    {processingId === sale.id ? (
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-5 h-5" />
                                    )}
                                  </button>
                                )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="border-t border-secondary-lightGray px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-xs sm:text-sm text-secondary-gray text-center sm:text-left">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} sales
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 bg-white border border-secondary-lightGray rounded text-xs sm:text-sm hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="whitespace-nowrap">Previous</span>
                    </button>
                    <span className="px-3 py-1 text-xs sm:text-sm text-secondary-gray flex items-center">
                      <span className="whitespace-nowrap">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-1 bg-white border border-secondary-lightGray rounded text-xs sm:text-sm hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="whitespace-nowrap">Next</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sale Detail Modal */}
        {selectedSaleId && (
          <SaleDetailModal
            saleId={selectedSaleId}
            onClose={() => setSelectedSaleId(null)}
            onReverse={() => {
              const reversedId = selectedSaleId;
              setSelectedSaleId(null);
              if (activeTab === 'all') {
                setSales((prev) => prev.filter((s) => s.id !== reversedId));
                setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
              }
            }}
          />
        )}

        {/* Receipt Preview Modal */}
        {receiptPreview && (
          <ReceiptPreviewModal
            saleId={receiptPreview.saleId}
            receiptNumber={receiptPreview.receiptNumber}
            customerName={receiptPreview.customerName}
            customerPhone={receiptPreview.customerPhone}
            customerEmail={receiptPreview.customerEmail}
            totalAmount={receiptPreview.totalAmount}
            saleDate={receiptPreview.saleDate}
            onClose={() => setReceiptPreview(null)}
          />
        )}

        {/* Bank Channel Modal for Updates */}
        {showBankModal && pendingUpdateSale && (
          <BankChannelModal
            isOpen={showBankModal}
            onClose={() => {
              setShowBankModal(false);
              setPendingUpdateSale(null);
            }}
            onSelect={handleBankChannelSelect}
            onSkip={() => {
              setShowBankModal(false);
              setPendingUpdateSale(null);
            }}
            saleAmount={pendingUpdateSale.amount_paid}
            paymentMethod={pendingUpdateSale.payment_method}
          />
        )}
      </div>
    </div>
  );
};

export default SalesList;
